import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import { StatusPill } from '@components/StatusPill';

import { useAppVersionHistory } from '@hooks/api/app-version';

import { formatDate } from '@utils/format';

import type {
  AppVersionFieldsFragment,
  AppVersionSummaryFieldsFragment,
} from 'generated/app-version.generated';
import { AppVersionStatus } from 'generated/types';

const messages = defineMessages({
  noHistory: {
    id: 'admin.configurations.noHistory',
    defaultMessage: 'No published releases yet',
  },
  historyLoadError: {
    id: 'admin.configurations.historyLoadError',
    defaultMessage: 'Could not load release history. Please try again.',
  },
  minimum: {
    id: 'admin.configurations.minimum',
    defaultMessage: 'Minimum',
  },
});

function sortReleasesNewestFirst(
  releases: AppVersionFieldsFragment[]
): AppVersionFieldsFragment[] {
  return [...releases].sort(
    (left, right) => right.version_sequence - left.version_sequence
  );
}

interface ReleaseHistoryProps {
  summary: AppVersionSummaryFieldsFragment;
}

function ReleaseHistory({ summary }: ReleaseHistoryProps) {
  const { formatMessage } = useIntl();
  const { palette } = useTheme();
  const { data, loading, error } = useAppVersionHistory({
    app: summary.app,
    platform: summary.platform,
    status: AppVersionStatus.published,
  });

  const releases = useMemo(
    () => sortReleasesNewestFirst(data?.listAppVersions ?? []),
    [data?.listAppVersions]
  );

  return (
    <ContentLoader loading={loading}>
      <Box data-testid="release-history">
        {error && !loading ? (
          <Typography
            color="error"
            data-testid="release-history-load-error"
            variant="body2"
          >
            {formatMessage(messages.historyLoadError)}
          </Typography>
        ) : releases.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            {formatMessage(messages.noHistory)}
          </Typography>
        ) : (
          <Stack spacing={1.5} sx={{ minWidth: 0 }}>
            {releases.map((release) => {
              const isEffectiveMinimum =
                Boolean(summary.minimum_version) &&
                release.version_number === summary.minimum_version;
              const releaseNotes = release.release_notes?.trim();

              return (
                <Box
                  key={release.app_version_id}
                  data-testid={`history-item-${release.version_number}`}
                  sx={{
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                    minWidth: 0,
                    pt: 1.5,
                  }}
                >
                  <Stack
                    alignItems="center"
                    direction="row"
                    flexWrap="wrap"
                    gap={1}
                    mb={releaseNotes ? 0.5 : 0}
                  >
                    <StatusPill
                      color={palette.primary.main}
                      name={`v${release.version_number}`}
                      variant="primary"
                    />
                    {isEffectiveMinimum ? (
                      <StatusPill
                        color={palette.warning.main}
                        name={formatMessage(messages.minimum)}
                        variant="primary"
                      />
                    ) : null}
                    <Typography color="text.secondary" variant="body2">
                      {release.release_date
                        ? formatDate({
                            date: release.release_date,
                            formatter: 'date',
                            utc: true,
                          })
                        : '-'}
                    </Typography>
                  </Stack>
                  {releaseNotes ? (
                    <Typography
                      color="text.secondary"
                      sx={{
                        maxWidth: '100%',
                        overflowWrap: 'anywhere',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                      variant="body2"
                    >
                      {releaseNotes}
                    </Typography>
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </ContentLoader>
  );
}

export default ReleaseHistory;
